// src/pages/admincomponents/adminStyles.js — V18 styles shared by the admin views.
import styled, { css, keyframes } from 'styled-components';
import { color, font } from '../../theme/tokens';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const rise = keyframes`
  from { transform: translateY(-12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const cornerTicks = css`
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }
`;

/* ---------- panel ---------- */
export const Panel = styled.section`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding: 28px 32px;
    background: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    font-family: ${font.body};
    ${cornerTicks}

    @media (max-width: 640px) { padding: 20px 16px; }
`;

export const PanelHeader = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${color.line};
`;

export const PanelTitle = styled.h2`
    margin: 0;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 0.2em;
    color: ${color.text};
`;

export const SubTitle = styled.h3`
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 700;
    font-size: 17px;
    letter-spacing: 0.16em;
    color: ${color.text};
`;

export const MetaText = styled.div`
    font-size: 13px;
    letter-spacing: 0.08em;
    color: ${color.text3};
    text-align: right;
    line-height: 1.7;
    b { font-weight: 400; color: ${color.text2}; }
    code { font-family: ${font.mono}; color: ${color.text2}; }
`;

export const StatusText = styled.p`
    margin: 0;
    padding: 20px 0;
    font-family: ${font.display};
    font-size: 14px;
    letter-spacing: 0.24em;
    color: ${color.text3};
`;

export const ErrorNote = styled.p`
    margin: 0 0 16px;
    padding: 10px 14px;
    border-left: 2px solid ${color.bloodHi};
    background: rgba(200, 50, 62, 0.08);
    font-size: 14px;
    color: ${color.bloodHi};
`;

/* ---------- buttons ---------- */
export const AdminButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-sizing: border-box;
    min-height: 36px;
    padding: 0 16px;
    border: 1px solid ${color.line2};
    border-radius: 0;
    background: transparent;
    color: ${color.text2};
    font-family: ${font.body};
    font-size: 13px;
    letter-spacing: 0.14em;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

    &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.text}; }
    &:disabled { opacity: 0.45; cursor: not-allowed; }

    ${({ $variant }) => $variant === 'primary' && css`
        border-color: ${color.blood};
        background: ${color.blood};
        color: ${color.onBlood};
        font-family: ${font.display};
        font-weight: 700;
        &:hover:not(:disabled) { border-color: ${color.bloodHi}; background: ${color.bloodHi}; color: ${color.onBlood}; }
    `}

    ${({ $variant }) => $variant === 'danger' && css`
        border-color: rgba(224, 70, 79, 0.5);
        color: ${color.bloodHi};
        &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.textBright}; background: rgba(200, 50, 62, 0.14); }
    `}

    ${({ $small }) => $small && css`
        min-height: 30px;
        padding: 0 10px;
        font-size: 12px;
    `}
`;

export const IconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid ${color.line2};
    border-radius: 0;
    background: transparent;
    color: ${color.text2};
    font-size: 13px;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;
    &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.text}; }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export const LinkButton = styled.button`
    padding: 0;
    border: none;
    background: none;
    color: ${color.text};
    font: inherit;
    text-align: left;
    cursor: pointer;
    border-bottom: 1px solid transparent;
    transition: color 0.2s ease, border-color 0.2s ease;
    &:hover { color: ${color.bloodHi}; border-bottom-color: rgba(224, 70, 79, 0.45); }
`;

export const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

/* ---------- tables ---------- */
export const TableWrap = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${color.line};
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    font-size: 14px;
    color: ${color.text};
`;

export const Th = styled.th`
    padding: 12px 14px;
    background: ${color.surface2};
    border-bottom: 1px solid ${color.line2};
    font-weight: 400;
    font-size: 12px;
    letter-spacing: 0.2em;
    color: ${color.text3};
    white-space: nowrap;
`;

export const Td = styled.td`
    padding: 12px 14px;
    border-bottom: 1px solid ${color.line};
    vertical-align: middle;
    color: ${({ $dim }) => ($dim ? color.text2 : color.text)};
    ${({ $mono }) => $mono && css`font-family: ${font.mono}; font-size: 13px;`}
`;

export const Tr = styled.tr`
    transition: background-color 0.15s ease;
    &:hover { background: ${color.surface2}; }
    &:last-child td { border-bottom: none; }
`;

/* ---------- modal ---------- */
export const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(7, 2, 5, 0.8);
    animation: ${fadeIn} 0.25s ease;
`;

export const ModalPanel = styled.div`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: ${({ $wide }) => ($wide ? 640 : 520)}px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    padding: 32px 36px;
    background: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    font-family: ${font.body};
    animation: ${rise} 0.3s ease-out;
    ${cornerTicks}

    @media (max-width: 640px) { padding: 22px 18px; }
`;

export const ModalTitle = styled.h2`
    margin: 0 0 24px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 0.16em;
    color: ${color.text};
`;

export const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
`;

/* ---------- form ---------- */
export const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

export const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

export const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

export const Label = styled.label`
    font-size: 13px;
    letter-spacing: 0.18em;
    color: ${color.text2};
    small { font-family: ${font.mono}; letter-spacing: 0.02em; color: ${color.text3}; margin-left: 6px; }
`;

const fieldBase = css`
    box-sizing: border-box;
    width: 100%;
    padding: 0 14px;
    background: rgba(7, 2, 5, 0.7);
    border: 1px solid ${color.line2};
    border-radius: 0;
    color: ${color.text};
    font-family: ${font.body};
    font-size: 15px;
    color-scheme: dark;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    &::placeholder { color: ${color.text3}; }
    &:focus { outline: none; border-color: ${color.bloodHi}; box-shadow: 0 0 0 3px rgba(224, 70, 79, 0.18); }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

export const TextInput = styled.input`
    ${fieldBase}
    height: 44px;
    &[type='number'], &[type='datetime-local'] { font-family: ${font.mono}; font-size: 14px; }
`;

export const Select = styled.select`
    ${fieldBase}
    height: 44px;
    background-color: ${color.void};
`;

export const TextArea = styled.textarea`
    ${fieldBase}
    min-height: 120px;
    padding: 12px 14px;
    line-height: 1.7;
    resize: vertical;
`;

export const Fieldset = styled.fieldset`
    margin: 0;
    padding: 16px 18px 6px;
    border: 1px solid ${color.line};
    min-width: 0;
    legend {
        padding: 0 8px;
        font-family: ${font.display};
        font-size: 14px;
        letter-spacing: 0.2em;
        color: ${color.bloodHi};
    }
`;

export const CheckLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: ${({ $disabled }) => ($disabled ? color.text3 : color.text)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    input { width: 16px; height: 16px; margin: 0; accent-color: ${color.blood}; }
`;
